import { useMemo } from "react";
import { Box, Stack } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { AddCircleRounded } from "@mui/icons-material";
import { IDeductionChargebackType, IPayItems } from "@/Settings/types/Common/model";
import SimpleDoubleChecking from "@Layouts/components/Buttons/SimpleDoubleChecking";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";

const containerSx = {
	"& > *:not(:last-child) .btn-add": { display: "none" }, // display add button only for last item
};

export const EMPTY_DEDUCTION_CHARGEBACK_ITEM = {
	chargebackCode: "",
	earningPayItem: "",
	deductionPayItem: "",
} as IDeductionChargebackType;

interface IDeductionsChargebackSelectorProps {
	items: IDeductionChargebackType[];
	selectableOptions: { code: string; description: string }[];
	isPending: boolean;
	position: string;
	onAdd: () => void;
	onChange: (item: IDeductionChargebackType, index: number) => void;
	onDelete: (index: number) => void;
	payItemsOptions: IPayItems[];
}

const DeductionsChargebackSelector = (props: IDeductionsChargebackSelectorProps) => {
	const { items = [], selectableOptions, isPending, onAdd, onChange, onDelete, payItemsOptions } = props;

	const displayItems = items.length === 0 ? [] : items;

	const getAvailableChargebackOptions = (currentIndex: number) => {
		const selectedCodes = items.map((item, idx) => (idx !== currentIndex ? String(item.chargebackCode) : null)).filter(Boolean);
		return selectableOptions.filter((option) => !selectedCodes.includes(option.code));
	};

	const addButtonEnabled = useMemo(() => {
		if (items.length === 0) return true;
		const lastItem = items.at(-1)!;
		const hasValidLastItem = !!lastItem.chargebackCode && !!lastItem.deductionPayItem;
		const hasAvailableOptions = !!getAvailableChargebackOptions(items.length).length;
		return hasValidLastItem && hasAvailableOptions;
	}, [items, selectableOptions]);

	const handlePayItemsChange = ({ item, index, values }: { item: IDeductionChargebackType; index: number; values: TSelectValue }) => {
		const earningPayItem = (values as IPayItems[])?.find((item) => item.group === "Earnings")?._id || "";
		const deductionPayItem = (values as IPayItems[])?.find((item) => item.group === "Deductions")?._id || "";
		onChange({ ...item, earningPayItem, deductionPayItem }, index);
	};

	return (
		<Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={containerSx}>
			{displayItems.map((item, index) => {
				const selectedPayItems = payItemsOptions.filter(
					(payItem) => payItem?._id === item?.earningPayItem || payItem?._id === item?.deductionPayItem
				);
				return (
					<Stack key={index} direction="column">
						<Stack direction="row" alignItems="center">
							<Box sx={{ width: 216, mr: 1.25 }}>
								<SmartSelector
									showBy="description"
									orderBy="description"
									options={getAvailableChargebackOptions(index)}
									selectBy="code"
									selected={item.chargebackCode}
									inputProps={{ placeholder: "Gross Chargeback" }}
									clearable
									disabled={isPending}
									onChangeAction={(value: any) => onChange({ ...item, chargebackCode: value?.code }, index)}
									error={!item.chargebackCode}
								/>
							</Box>
							<Box sx={{ width: 200 }}>
								<SmartSelector
									popperProps={{ className: "!z-[1350]" } as object}
									classes="!w-[200px] [&_.MuiChip-root]:!font-sans"
									tagsVariant="text"
									showBy="systemPayItem"
									selectBy="_id"
									orderBy="group"
									options={payItemsOptions}
									groupingProps={{
										groupBy: "group",
										defaultLabel: "Unknown",
										sticky: true,
										specificProperties: {
											Earnings: {
												classes: "!text-primary !text-lg",
												sx: { fontVariant: "!important" },
											},
											Deductions: {
												classes: "!text-primary !text-lg",
												sx: { fontVariant: "!important" },
											},
										},
									}}
									selected={selectedPayItems}
									multiple
									inputProps={{
										placeholder: "Pay Items",
									}}
									onChangeAction={(values) => {
										handlePayItemsChange({ item, index, values });
									}}
									error={!item.deductionPayItem}
									disabled={isPending}
								/>
							</Box>
							<SimpleDoubleChecking
								disabled={isPending}
								color="var(--gray-blue-color)"
								confirmationButtonColor="var(--gray-blue-color)"
								icon={<CloseRoundedIcon fontSize="small" />}
								iconHover={<CloseRoundedIcon fontSize="small" />}
								confirmationIcon={<TrashIcon />}
								onDeleteAction={() => onDelete(index)}
							/>
						</Stack>
					</Stack>
				);
			})}
			<SimpleIconButton
				className="btn-add !ml-[4px]"
				color="var(--primary-color)"
				disabled={isPending || !addButtonEnabled}
				icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
				iconHover={<AddCircleRounded fontSize="medium" />}
				onClick={onAdd}
			/>
		</Stack>
	);
};

export default DeductionsChargebackSelector;
